<template>
    <UpdateRequestBanner v-if="showUpdateRequestBanner" @click="onUpdateRequestBannerClick" />
    <div class="w-full h-screen flex relative">
        <NavigationSidebar
            class="flex-none"
            :user="user"
            :is-expanded="isExpanded"
            :deployment-info="deploymentInfo"
            :show-environment="props.showEnvironmentName"
            :main-navigation-headline="$t('main-navigation-headline')"
            :user-section-headline="$t('user-section-headline')"
            :deployment-info-headline="$t('deployment-info-headline')"
            @toggle-is-expanded="isExpanded = !isExpanded"
            @logout="auth.logout()"
        >
            <template v-if="isExpanded">
                <NavigationItem target="/">
                    <AppsIcon class="w-5 h-5" />
                    {{ $t('dashboard') }}
                </NavigationItem>

                <NavigationItem target="/locations">
                    <BuildingIcon class="w-5 h-5" />
                    {{ $t('location-wiki') }}
                </NavigationItem>

                <NavigationItem target="/events">
                    <BallotIcon class="w-5 h-5" />
                    {{ $t('event-wiki') }}
                </NavigationItem>

                <NavigationItem target="/scenarios">
                    <BrainIcon class="w-5 h-5" />
                    {{ $t('scenarios') }}
                </NavigationItem>

                <NavigationItem target="/allocation-designer">
                    <TacticIcon class="w-5 h-5" />
                    {{ $t('allocation-designer') }}
                </NavigationItem>

                <NavigationItem target="/projects">
                    <ConstructionIcon class="w-5 h-5" />
                    {{ $t('project-wiki') }}
                </NavigationItem>

                <NavigationItem target="/external-events">
                    <AirplaneIcon class="w-5 h-5" />
                    {{ $t('external-events') }}
                </NavigationItem>

                <NavigationItem target="/calendar">
                    <EventIcon class="w-5 h-5" />
                    {{ $t('calendar') }}
                </NavigationItem>

                <NavigationItem target="/users">
                    <UsersIcon class="w-5 h-5" />
                    {{ $t('users') }}
                </NavigationItem>
            </template>

            <template v-if="!isExpanded">
                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/">
                            <AppsIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('dashboard') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/locations">
                            <BuildingIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('location-wiki') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/events">
                            <BallotIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('event-wiki') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/scenarios">
                            <BrainIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('scenarios') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/allocation-designer">
                            <TacticIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('allocation-designer') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/projects">
                            <ConstructionIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('projects') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/external-events">
                            <AirplaneIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('external-events') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/calendar">
                            <EventIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('calendar') }}</template>
                </Tooltip>

                <Tooltip>
                    <template #trigger="tooltipProps">
                        <NavigationItem target="/users">
                            <UsersIcon class="w-5 h-5" v-bind="tooltipProps" />
                        </NavigationItem>
                    </template>

                    <template #text>{{ $t('users') }}</template>
                </Tooltip>
            </template>
        </NavigationSidebar>

        <div class="w-full h-screen overflow-y-auto">
            <EnvironmentWarnBanner v-if="props.hasEnvironmentNameMismatch" environment="dev" />
            <router-view />
        </div>
    </div>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import EnvironmentWarnBanner from '@/components/EnvironmentWarnBanner/EnvironmentWarnBanner.vue';
import AirplaneIcon from '@/components/Icon/AirplaneIcon.vue';
import AppsIcon from '@/components/Icon/AppsIcon.vue';
import BallotIcon from '@/components/Icon/BallotIcon.vue';
import BrainIcon from '@/components/Icon/BrainIcon.vue';
import BuildingIcon from '@/components/Icon/BuildingIcon.vue';
import ConstructionIcon from '@/components/Icon/ConstructionIcon.vue';
import EventIcon from '@/components/Icon/EventIcon.vue';
import TacticIcon from '@/components/Icon/TacticIcon.vue';
import UsersIcon from '@/components/Icon/UsersIcon.vue';
import NavigationItem from '@/components/NavigationSidebar/NavigationItem.vue';
import NavigationSidebar from '@/components/NavigationSidebar/NavigationSidebar.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import UpdateRequestBanner from '@/components/UpdateRequestBanner/UpdateRequestBanner.vue';
import { useVersionInfo } from '@/utils/hooks/useVersionInfo';
import { useStorage } from '@vueuse/core';
import { useFluent } from 'fluent-vue';
import { computed, ref, watch } from 'vue';

type BaseLayoutProps = {
    showEnvironmentName: boolean;
    hasEnvironmentNameMismatch: boolean;
    versionJsonUrl: string;
};

const props = defineProps<BaseLayoutProps>();

// token refresh for logged-in users every minute
auth.useRegularTokenRefresh();

const { $t } = useFluent();

const isExpanded = useStorage('navigation-sidebar-expanded', true);
const showUpdateRequestBanner = ref(false);

const { deploymentInfo, versionTimestamp, execute } = useVersionInfo(props.versionJsonUrl);

function handleReload() {
    localStorage.setItem('version', versionTimestamp.value);
    showUpdateRequestBanner.value = false;
}

// fetch version json every 10 minutes
setInterval(execute, 1000 * 60 * 10);
window.onbeforeunload = () => handleReload();

function checkVersionTimestamp() {
    const localTimestamp = localStorage.getItem('version');
    if (!localTimestamp) {
        localStorage.setItem('version', versionTimestamp.value);
    } else {
        if (versionTimestamp.value !== localTimestamp) {
            showUpdateRequestBanner.value = true;
        }
    }
}

watch(versionTimestamp, checkVersionTimestamp);

const currentUser = auth.getUser();

// TODO compute avatarUrl for current user, based on login provider
// get avatarUrl already in backend!
const user = computed(() => {
    return {
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        avatarUrl: '',
        avatarAlt: `${currentUser.firstName.at(0)?.toUpperCase()}${currentUser.lastName.at(0)?.toUpperCase()}`,
        profileLink: '/me',
    };
});

function onUpdateRequestBannerClick() {
    handleReload();
    window.location.reload();
}
</script>
